import { createElement, type ReactNode } from "react"
import { CORE_TRAY_CONFIGS } from "../components/tray/trayConfigs"
import type { TrayType } from "../store/managers/TrayManager"
import { browserModule } from "./browserModule"
import { appControlModule } from "./appControlModule"
import { sleepModule } from "./sleepModule"
import type { ProductModule, TaskLifecycleControlProps, TrayConfig } from "./types"

export const PRODUCT_MODULES: readonly ProductModule[] = [browserModule, sleepModule, appControlModule]

function assertUnique(values: readonly string[], kind: string): void {
    const seen = new Set<string>()
    for (const value of values) {
        if (seen.has(value)) throw new Error(`Duplicate ${kind}: ${value}`)
        seen.add(value)
    }
}

assertUnique(
    PRODUCT_MODULES.map((module) => module.id),
    "product module id"
)

function composeTrayConfigs(): readonly TrayConfig[] {
    const configs = [...CORE_TRAY_CONFIGS]
    for (const config of PRODUCT_MODULES.flatMap((module) => module.trayConfigs ?? [])) {
        if (!config.insertBefore) {
            configs.push(config)
            continue
        }
        const targetIndex = configs.findIndex((candidate) => candidate.id === config.insertBefore)
        if (targetIndex < 0) throw new Error(`Tray ${config.id} targets missing insertion anchor: ${config.insertBefore}`)
        configs.splice(targetIndex, 0, config)
    }
    return configs
}

export const TRAY_CONFIGS = composeTrayConfigs()

assertUnique(
    TRAY_CONFIGS.map((config) => config.id),
    "tray id"
)

const trayConfigsByID = new Map(TRAY_CONFIGS.map((config) => [config.id, config]))

export function getTrayConfig(id: TrayType): TrayConfig | undefined {
    return trayConfigsByID.get(id)
}

export function installProductModules(): () => void {
    const disposers = PRODUCT_MODULES.flatMap((module) => (module.install ? [module.install()] : []))
    return () => {
        for (let index = disposers.length - 1; index >= 0; index -= 1) disposers[index]()
    }
}

export function ProductModuleGlobalSurfaces(): JSX.Element {
    return <>{PRODUCT_MODULES.flatMap((module) => (module.globalSurfaces ?? []).map((Surface, index) => <Surface key={`${module.id}:${index}`} />))}</>
}

export function productModuleTaskLifecycleControls(props: TaskLifecycleControlProps): ReactNode {
    const controls = PRODUCT_MODULES.flatMap((module) =>
        (module.taskLifecycleControls ?? [])
            .filter((control) => control.isVisible(props))
            .map(({ component }, index) => createElement(component, { ...props, key: `${module.id}:${index}` }))
    )
    return controls.length > 0 ? controls : undefined
}

export function isProductModuleTaskSurfaceVisible(): boolean {
    return PRODUCT_MODULES.some((module) => module.taskLayout?.isSurfaceVisible() === true)
}

export function productModuleRightPaneID(): string | null {
    return PRODUCT_MODULES.find((module) => module.taskLayout?.isRightPaneDocked() === true)?.id ?? null
}
